import classNames from 'classnames';
import { useAppSelector } from 'hooks/useAppSelector';
import { useIsInternalUser } from 'hooks/useIsInternalUser';
import { FixedUnitReadonlyValueDefinition } from 'models/metadata/readonlyValue.definition';
import { useIntl } from 'react-intl';
import { QuoteInfoListItem } from 'routes/essential-workspace/components/quote-panel/quote/info-table/info-item';
import { QuoteId } from 'routes/essential-workspace/components/quote-panel/quote/info-table/quote-id';

import './style.scss';
import { useQuote } from 'routes/essential-workspace/hooks/use-quote';
import { selectEssentialsFormQuoteResult } from 'routes/essential-workspace/state/form.selectors';
import { getFormQuoteDetails } from 'state/selectors';
import { getSidePanelQuoteDetailsFieldsMetadata } from 'state/uiPanelState/sidePanelUi/sidePanelUi.selectors';
import { xlDateTimeFormatOptions } from 'utils/core/date';

export function QuoteInfoTable() {
  const { formId } = useQuote();
  const isInternalUser = useIsInternalUser();
  const quoteResult = useAppSelector(state => selectEssentialsFormQuoteResult(state, formId));
  const quoteDetails = useAppSelector(state => getFormQuoteDetails(state, 'essential', formId));
  const quoteDetailsMetadata = useAppSelector(state => getSidePanelQuoteDetailsFieldsMetadata(state, formId));

  const { formatDate, formatMessage, formatNumber } = useIntl();

  const tradingPriceUnit = (quoteDetailsMetadata?.['TradingPrice'] as FixedUnitReadonlyValueDefinition)?.unit ?? '';
  const tradingPrice = quoteDetails?.TradingPrice
    ? `${formatNumber(quoteDetails?.TradingPrice, { maximumFractionDigits: 4 })}${tradingPriceUnit}`
    : '';

  const pricedOn = quoteResult?.createdAt ? formatDate(quoteResult?.createdAt, xlDateTimeFormatOptions) : '';

  const xMarginUnit = (quoteDetailsMetadata?.['XMargin'] as FixedUnitReadonlyValueDefinition)?.unit ?? '';
  const xMargin = quoteDetails?.XMargin
    ? `${formatNumber(quoteDetails?.XMargin, { maximumFractionDigits: 4 })}${xMarginUnit}`
    : '';

  return (
    <ul className={classNames('list-group list-group-flush border-top border-opacity-40')} aria-label="Quote info">
      <QuoteInfoListItem
        label={formatMessage({ id: 'quoteDetails.ExternalQuoteId', defaultMessage: 'Quote ID' })}
        value={<QuoteId formId={formId} />}
      />

      {Boolean(pricedOn) && (
        <QuoteInfoListItem
          label={formatMessage({ id: 'quoteDetails.PricedTime', defaultMessage: 'Priced time' })}
          value={pricedOn}
        />
      )}

      {isInternalUser && Boolean(tradingPrice) && (
        <QuoteInfoListItem
          label={formatMessage({ id: 'quoteDetails.TradingPrice', defaultMessage: 'Trading Price' })}
          value={tradingPrice}
        />
      )}

      {isInternalUser && Boolean(xMargin) && (
        <QuoteInfoListItem
          label={formatMessage({ id: 'quoteDetails.XMargin', defaultMessage: 'X Margin' })}
          value={xMargin}
        />
      )}

      <span className="divider bg-secondary bg-opacity-20 quote-list-item-divider" />
    </ul>
  );
}
